// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import type { Map as MapInterface } from '@daml/types';

// MarketData passed to Margin Calls requires DAML Map type. It looks
// like it doesn't come with actual implementation, so we have this facade
// on top of regular JavaScript map to match DAML typings.
export class DamlMap<K, V> implements MapInterface<K, V> {
  private readonly map = new Map<K, V>();

  delete(k: K): MapInterface<K, V> {
    this.map.delete(k);
    return this;
  }

  entries(): Iterator<[K, V], undefined, undefined> {
    return this.map.entries();
  }

  entriesArray(): [K, V][] {
    return Array.from(this.map.entries());
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  forEach<T, U>(f: (value: V, key: K, map: MapInterface<K, V>) => T, _u?: U): void {
    this.map.forEach((v, k) => {
      f(v, k, this);
    });
  }

  get(k: K): V | undefined {
    return this.map.get(k);
  }

  has(k: K): boolean {
    return this.map.has(k);
  }

  keys(): Iterator<K, undefined, undefined> {
    return this.map.keys();
  }

  set(k: K, v: V): DamlMap<K, V> {
    this.map.set(k, v);
    return this;
  }

  values(): Iterator<V, undefined, undefined> {
    return this.map.values();
  }
}
